import { FC, useContext, useEffect, useState, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  TextField,
} from "@mui/material";
import {
  useForm,
  Controller,
  FormProvider,
  SubmitHandler,
  Path,
} from "react-hook-form";
import __ from "src/utils/Translations";
import { Loader } from "src/Components/Loader/Loader";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { styles } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings/structureSettingsStyle";
import AutocompleteManager from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteManager";
import AutocompleteEmployees from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteEmployees";
import AutocompleteMainArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteMainArea";
import AutocompleteAdditionalArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteAdditionalArea";
import useChangeArea from "src/Pages/Company/OrganizationStructure/api/useChangeArea";
import {
  Employee,
  IArea,
  ICreateArea,
  IFormData,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { createEditAreaScheme } from "src/Pages/Company/OrganizationStructure/Areas/createEditArea.scheme";
import { dynamicNotification } from "src/utils/Notifications";
import { MAX_LENGTH_DESCRIPTION } from "src/Pages/Company/OrganizationStructure/Areas/constants/areaDescriptionLimit";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useDeleteArea from "src/Pages/Company/OrganizationStructure/api/useDeleteArea";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructureAreaDelete } from "src/utils/RoleBasedSecurity/permissions";
import { getErrorMessages } from "src/Pages/Company/OrganizationStructure/utils/errors/errorMessages";
import { AxiosError } from "axios";
import {
  BackendError,
  handleBackendErrors,
} from "src/Pages/Company/OrganizationStructure/utils/errors/handleBackendErrors";
import useUnsavedChangesGuard from "src/Pages/Company/OrganizationStructure/utils/useUnsavedChangesGuard";
import { IStructureData } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/StructureData.schema";
import useAnimationFrame from "src/Pages/Company/OrganizationStructure/utils/useAnimationFrame";
import useIsAhr from "src/utils/hooks/useIsAhr";

interface IEditArea {
  areaData: IArea;
  exitEditMode: (...args: boolean[]) => void;
  structureId: string;
  structureData: IStructureData | undefined;
}

const EditArea: FC<IEditArea> = ({
  areaData,
  exitEditMode,
  structureId,
  structureData,
}) => {
  const { scrollTop } = useAnimationFrame();
  const companyId = useCompanyId();
  const { areaId } = useParams<{ areaId: string }>();
  const history = useHistory();
  const isAhr = useIsAhr();
  const isMember: boolean = areaData?.manager?.area?.id === areaId;
  const [charCount, setCharCount] = useState(areaData.description?.length || 0);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const hasPermissionsStructureAreaDelete = usePermission(
    organizationStructureAreaDelete
  );

  const defaultOption: Employee = {
    id: areaData?.manager?.id ?? null,
    area: areaData?.manager?.area.label ?? null,
    avatar: areaData?.manager
      ? areaData.manager.avatar || null
      : structureData?.avatarLink || null,
    initials: areaData?.manager?.initials ?? null,
    position: areaData?.manager?.position.label ?? null,
    name: (areaData?.manager?.name || structureData?.name) ?? null,
    fk: areaData?.manager?.fk ?? null,
    additionalDisplayArea: null,
    presenceInformation: areaData?.manager?.presenceInformation,
  };

  const defaultMainArea = {
    id: areaData.parent?.id || null,
    label: areaData.parent?.label || structureData?.name,
  };

  const defaultAdditionalArea = {
    id: areaData.additionalDisplayArea?.id || null,
    label: areaData.additionalDisplayArea?.label || "",
  };

  const formMethods = useForm<IFormData>({
    resolver: standardSchemaResolver(createEditAreaScheme),
    defaultValues: {
      name: areaData?.name ?? undefined,
      manager: defaultOption,
      description: areaData?.description ?? undefined,
      parent: defaultMainArea,
      additionalDisplayArea: defaultAdditionalArea,
      members: areaData?.members ?? [],
      managerAsMember: isMember,
    },
  });

  const unblockRef = useRef<(() => void) | null>(null);
  const nextLocationRef = useRef<any>(null);
  const proceedNavigationRef = useRef<(() => void) | null>(null);

  const { control, handleSubmit, setError, getValues } = formMethods;

  const { mutate, error } = useChangeArea(
    companyId,
    structureId!,
    areaId,
    exitEditMode
  );

  useEffect(() => {
    if (!error) return;

    setIsFormSubmitted(false);

    const errorMessages = getErrorMessages();
    const axiosError = error as AxiosError<{ errors: BackendError[] }>;
    const backendErrors = axiosError.response?.data?.errors || [];
    const processedErrors = handleBackendErrors(backendErrors, errorMessages);

    processedErrors.forEach(({ field, message }) => {
      setError(field as Path<IFormData>, { type: "server", message });
    });
  }, [error, setError]);

  const confirmLeaving = useUnsavedChangesGuard({
    onExit: () => {
      if (proceedNavigationRef.current) proceedNavigationRef.current();
    },
    setModal,
    resetModal,
  });

  useEffect(() => {
    unblockRef.current = history.block((tx) => {
      if (isFormSubmitted) return undefined;

      const values = getValues();
      const hasUnsavedChanges = Object.values(values).some(
        (v) => v !== "" && v != null
      );

      if (!hasUnsavedChanges) return undefined;

      nextLocationRef.current = tx;

      proceedNavigationRef.current = () => {
        if (unblockRef.current) unblockRef.current();
        history.push(tx.pathname);
      };

      confirmLeaving();

      return false;
    });

    return () => {
      if (unblockRef.current) unblockRef.current();
    };
  }, [history, getValues, confirmLeaving, isFormSubmitted]);

  const onSubmit: SubmitHandler<IFormData> = (formData) => {
    const validationResult = createEditAreaScheme.safeParse(formData);

    if (!validationResult.success) {
      dynamicNotification(__("Błąd walidacji formularza"), "error");
      return;
    }

    const payload: ICreateArea = {
      ...formData,
      ...validationResult.data,
      manager:
        formData.manager?.id === "structure"
          ? null
          : formData.manager?.id ?? null,
      parent: formData.parent?.id || null,
      additionalDisplayArea: formData.additionalDisplayArea?.id || null,
      description: formData.description ?? null,
      members: formData.members?.map((member) => member.id) || [],
      inheritManager: formData.manager == null || formData.manager.id == null,
    };
    mutate(payload);
    scrollTop();
  };

  const handleDeleteArea = () => {
    setIsFormSubmitted(true);
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    deleteArea();
    resetModal();
  };

  const { mutate: deleteArea } = useDeleteArea({
    companyId,
    structureId,
    areaId,
  });

  if (!structureId) {
    return <Loader />;
  }

  return (
    <>
      <FormProvider {...formMethods}>
        <Paper
          sx={{
            ...styles.formWrapper,
            marginBottom: "1.4rem",
            padding: 4,
          }}
        >
          <Typography sx={{ fontSize: "20px" }} fontWeight="fontWeightBold">
            {__("Edytuj obszar")}
          </Typography>
          <Divider sx={{ marginBottom: "32px" }} />

          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!error}
                    helperText={error ? error.message : null}
                    label={__("Nazwa obszaru")}
                  />
                )}
              />

              <AutocompleteManager
                companyId={companyId!}
                structureId={structureId}
                helperText={__(
                  "Jeżeli pozostawisz to pole puste, to zarządzającym tego obszaru stanie się osoba zarządzająca nadrzędnym obszarem."
                )}
                isEdit
              />

              <AutocompleteMainArea
                companyId={companyId}
                structureId={structureId}
                areaId={areaId}
                isEdit
              />

              <AutocompleteAdditionalArea
                companyId={companyId}
                structureId={structureId}
              />

              <Controller
                name="description"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!error}
                    helperText={`${charCount}/${MAX_LENGTH_DESCRIPTION} ${
                      error ? ` - ${__(error.message)}` : ""
                    }`}
                    label={__("Opis")}
                    multiline
                    rows={4}
                    slotProps={{
                      htmlInput: {
                        maxLength: MAX_LENGTH_DESCRIPTION,
                      },
                    }}
                    onChange={(event) => {
                      setCharCount(event.target.value.length);
                      field.onChange(event);
                    }}
                  />
                )}
              />

              <Typography fontWeight={700}>
                {__("Dodawanie pracowników")}:
              </Typography>

              <AutocompleteEmployees
                companyId={companyId}
                structureId={structureId}
              />

              <Divider />
              <Box sx={{ display: "flex", gap: "24px" }}>
                <Button
                  variant="outlined"
                  type="button"
                  size="large"
                  sx={{ flexGrow: 1 }}
                  onClick={() =>
                    history.push(
                      isAhr
                        ? `/ahr/organization-structure/configure-structure/structure-areas`
                        : `/company/edit/${companyId}/organization-structure/configure-structure/structure-areas`
                    )
                  }
                >
                  {__("Anuluj")}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  size="large"
                  sx={{ flexGrow: 1 }}
                >
                  {__("Zapisz zmiany")}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </FormProvider>
      {hasPermissionsStructureAreaDelete && (
        <Paper
          sx={{
            ...styles.formWrapper,
            marginBottom: "1.4rem",
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            padding: 4,
          }}
        >
          <Typography
            sx={{
              color: "#118CC6",
              textTransform: "uppercase",
              fontSize: "13px",
              fontWeight: 800,
              letterSpacing: 1.13,
              lineHeight: 1.5,
            }}
          >
            {__("Usuwanie Obszaru")}:
          </Typography>
          <Button
            variant="text"
            onClick={() =>
              setModal({
                open: true,
                title: __("Usuń obszar"),
                description: __(
                  `Czy na pewno chcesz usunąć obszar ${areaData?.name}?`
                ),
                actions: [
                  {
                    label: __("Anuluj"),
                    variant: "outlined",
                    callbackFn: resetModal,
                  },
                  {
                    label: __("Tak, Usuń"),
                    color: "error",
                    variant: "contained",
                    callbackFn: handleDeleteArea,
                  },
                ],
              })
            }
            sx={{
              color: "#B91717",
              fontSize: "17px",
              lineHeight: 1.5,
              fontWeight: 400,
              padding: 0,
            }}
          >
            {__("Usuń obszar")}
          </Button>
        </Paper>
      )}
    </>
  );
};

export default EditArea;
